import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PushPinIcon from "@mui/icons-material/PushPin";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api from "../../api";
import { toast } from "react-toastify";

const CARD_SX = { p: 2, borderRadius: 3, border: "1px solid #E2E8F0", boxShadow: "none", display: "flex", flexDirection: "column", gap: 1, minHeight: 0 };

const move = (list, index, delta) => {
  const next = list.slice();
  const target = index + delta;
  if (target < 0 || target >= next.length) return list;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

/**
 * A drag-to-reorder list (shop redesign, 29 Oct 2026). Each row gets a drag handle; the arrows
 * on the rows stay as the keyboard and precise option. `onMove(from, to)` gets list indexes.
 */
function SortableList({ items, getId, onMove, renderItem }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const ids = items.map((item) => String(getId(item)));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from >= 0 && to >= 0) onMove(from, to);
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {items.map((item, index) => (
          <SortableItem key={ids[index]} id={ids[index]}>
            {(handle) => renderItem(item, index, handle)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const handle = (
    <IconButton size="small" aria-label="Drag to reorder" {...attributes} {...listeners} sx={{ cursor: "grab", color: "#94A3B8", touchAction: "none" }}>
      <DragIndicatorIcon fontSize="small" />
    </IconButton>
  );
  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        position: "relative",
        zIndex: isDragging ? 2 : "auto",
        opacity: isDragging ? 0.85 : 1,
        boxShadow: isDragging ? "0 8px 20px rgba(26,35,52,0.15)" : "none",
        borderRadius: 2,
      }}
    >
      {children(handle)}
    </Box>
  );
}

/** Red within 14 days, amber within 45, grey later. */
function examSoon(date) {
  const days = Math.ceil((new Date(`${date}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
  return days <= 14 ? "#b71c1c" : days <= 45 ? "#9a4a00" : "text.secondary";
}

function OrderRow({ selected, onSelect, onUp, onDown, children, highlight, handle }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 44,
        px: 1,
        borderRadius: 2,
        border: selected ? "2px solid #4450A5" : highlight ? "1px solid #C7CBF0" : "1px solid #E2E8F0",
        bgcolor: selected ? "#F7F8FE" : "#fff",
      }}
    >
      {handle || null}
      <Box
        component={onSelect ? "button" : "div"}
        type={onSelect ? "button" : undefined}
        onClick={onSelect}
        sx={{ flex: 1, minWidth: 0, textAlign: "left", border: 0, bgcolor: "transparent", cursor: onSelect ? "pointer" : "default", font: "inherit", py: 1 }}
      >
        {children}
      </Box>
      {onUp ? (
        <IconButton size="small" aria-label="Move up" onClick={onUp}>
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
      ) : null}
      {onDown ? (
        <IconButton size="small" aria-label="Move down" onClick={onDown}>
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );
}

/**
 * Admin → Display order (21 Oct 2026 meeting): the order parents see areas, consortium groups
 * and pinned papers / mock exams on Practice Papers, Mock Exams and the school finder.
 * Backed by GET/PUT admin/display-order and admin/display-order/items.
 */
function SchoolsAndAreas() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [consortiumFirst, setConsortiumFirst] = useState(true);
  const [schoolSort, setSchoolSort] = useState("rank");
  const [defaultSchoolCategory, setDefaultSchoolCategory] = useState("independent");
  const [regions, setRegions] = useState([]);
  const [consortia, setConsortia] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [selectedConsortiumId, setSelectedConsortiumId] = useState(null);
  const [schoolScope, setSchoolScope] = useState("consortium");
  const [itemType, setItemType] = useState("papers");
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("admin/display-order");
      const d = res.data?.data || {};
      setConsortiumFirst(Boolean(d.consortium_first));
      setSchoolSort(["pupils", "exam_date"].includes(d.school_sort) ? d.school_sort : "rank");
      setDefaultSchoolCategory(
        ["grammar", "both", "all"].includes(d.default_school_category) ? d.default_school_category : "independent"
      );
      setRegions(d.regions || []);
      setConsortia(d.consortia || []);
      setSelectedRegionId((prev) => prev ?? d.regions?.[0]?.id ?? null);
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not load the display order.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const region = regions.find((r) => r.id === selectedRegionId) || null;

  // A region's groups follow the global consortium order, so moving a group here reorders it
  // everywhere it appears.
  const regionGroups = useMemo(() => {
    if (!region) return [];
    const rank = new Map(consortia.map((c, i) => [c.id, i]));
    const groups = [...region.consortia].sort((a, b) => (rank.get(a.id) ?? 999) - (rank.get(b.id) ?? 999));
    return groups;
  }, [region, consortia]);

  useEffect(() => {
    setSelectedConsortiumId(regionGroups[0]?.id ?? null);
    setSchoolScope(regionGroups[0] ? "consortium" : "other");
  }, [selectedRegionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const schoolRows = useMemo(() => {
    if (!region) return [];
    const source = schoolScope === "other"
      ? region.other_schools || []
      : (region.consortia || []).find((g) => g.id === selectedConsortiumId)?.schools || [];
    const rows = [...source];
    if (schoolSort === "pupils") {
      rows.sort(
        (a, b) =>
          (Number(b.pupil_count) || 0) - (Number(a.pupil_count) || 0) ||
          String(a.name).localeCompare(String(b.name))
      );
    } else if (schoolSort === "exam_date") {
      // Soonest next exam first; schools with no upcoming date keep their rank order after them.
      rows.sort((a, b) => (a.next_exam_date || "9999-12-31").localeCompare(b.next_exam_date || "9999-12-31"));
    }
    return rows;
  }, [region, schoolScope, selectedConsortiumId, schoolSort]);

  const loadItems = useCallback(async () => {
    if (!selectedConsortiumId) {
      setItems([]);
      return;
    }
    setItemsLoading(true);
    try {
      const res = await api.get("admin/display-order/items", {
        params: { type: itemType, consortium_id: selectedConsortiumId },
      });
      setItems(res.data?.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not load items.");
      setItems([]);
    } finally {
      setItemsLoading(false);
    }
  }, [itemType, selectedConsortiumId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const moveRegion = (i, d) => {
    setRegions((list) => move(list, i, d));
    setDirty(true);
  };
  const dragRegion = (from, to) => {
    setRegions((list) => arrayMove(list, from, to));
    setDirty(true);
  };

  // A dragged group takes its new place in this area; the global order keeps every other
  // consortium where it was, and this area's groups fill their old slots in the new order.
  const dragGroup = (from, to) => {
    const reordered = arrayMove(regionGroups, from, to).map((g) => g.id);
    setConsortia((list) => {
      const slots = list.map((c, i) => (reordered.includes(c.id) ? i : -1)).filter((i) => i >= 0);
      const next = list.slice();
      reordered.forEach((id, k) => {
        next[slots[k]] = list.find((c) => c.id === id);
      });
      return next;
    });
    setDirty(true);
  };

  const dragSchool = (from, to) => {
    setRegions((list) =>
      list.map((r) => {
        if (r.id !== selectedRegionId) return r;
        if (schoolScope === "other") {
          return { ...r, other_schools: arrayMove(r.other_schools || [], from, to) };
        }
        return {
          ...r,
          consortia: (r.consortia || []).map((g) =>
            g.id === selectedConsortiumId ? { ...g, schools: arrayMove(g.schools || [], from, to) } : g
          ),
        };
      })
    );
    setDirty(true);
  };

  const moveGroup = (groupId, d) => {
    // Swap the group with its neighbour in this region, then reflect it in the global order.
    const idx = regionGroups.findIndex((g) => g.id === groupId);
    const neighbour = regionGroups[idx + d];
    if (!neighbour) return;
    setConsortia((list) => {
      const a = list.findIndex((c) => c.id === groupId);
      const b = list.findIndex((c) => c.id === neighbour.id);
      if (a < 0 || b < 0) return list;
      const next = list.slice();
      [next[a], next[b]] = [next[b], next[a]];
      return next;
    });
    setDirty(true);
  };

  const moveSchool = (index, delta) => {
    setRegions((list) =>
      list.map((r) => {
        if (r.id !== selectedRegionId) return r;
        if (schoolScope === "other") {
          return { ...r, other_schools: move(r.other_schools || [], index, delta) };
        }
        return {
          ...r,
          consortia: (r.consortia || []).map((g) =>
            g.id === selectedConsortiumId ? { ...g, schools: move(g.schools || [], index, delta) } : g
          ),
        };
      })
    );
    setDirty(true);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const schoolIds = [];
      regions.forEach((r) => {
        (r.consortia || []).forEach((g) => (g.schools || []).forEach((s) => schoolIds.push(s.id)));
        (r.other_schools || []).forEach((s) => schoolIds.push(s.id));
      });
      await api.put("admin/display-order", {
        consortium_first: consortiumFirst,
        school_sort: schoolSort,
        default_school_category: defaultSchoolCategory,
        region_ids: regions.map((r) => r.id),
        consortium_ids: consortia.map((c) => c.id),
        school_ids: schoolIds,
      });
      toast.success("Display order saved.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not save the order.");
    } finally {
      setSaving(false);
    }
  };

  const pinnedCount = items.filter((i) => i.sort_order != null).length;

  const saveItems = async (nextItems, unpinIds = []) => {
    const pinned = nextItems.filter((i) => i.sort_order != null).map((i) => i.id);
    try {
      await api.put("admin/display-order/items", { type: itemType, ids: pinned, unpin_ids: unpinIds });
      await loadItems();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not save the item order.");
    }
  };

  const moveItem = (i, d) => {
    // Only pinned items have a position; moving one keeps it inside the pinned block.
    if (i + d < 0 || i + d >= pinnedCount) return;
    const next = move(items, i, d);
    setItems(next);
    saveItems(next);
  };
  const dragItem = (from, to) => {
    const next = [...arrayMove(items.slice(0, pinnedCount), from, to), ...items.slice(pinnedCount)];
    setItems(next);
    saveItems(next);
  };

  const togglePin = (item) => {
    if (item.sort_order != null) {
      saveItems(items.filter((i) => i.id !== item.id), [item.id]);
    } else {
      const pinned = items.filter((i) => i.sort_order != null);
      saveItems([...pinned, { ...item, sort_order: pinned.length + 1 }]);
    }
  };

  const renderItemRow = (item, i, handle) => {
    const pinned = item.sort_order != null;
    return (
      <Box key={item.id} sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 44, px: 1, borderRadius: 2, border: pinned ? "1px solid #C7CBF0" : "1px solid #E2E8F0", bgcolor: pinned ? "#F7F8FE" : "#fff" }}>
        {handle}
        <IconButton size="small" aria-label={pinned ? "Unpin" : "Pin to top"} onClick={() => togglePin(item)} color={pinned ? "primary" : "default"}>
          <PushPinIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap title={item.name}>{item.name}</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>{item.school}</Typography>
        </Box>
        {pinned && (
          <>
            <IconButton size="small" aria-label="Move up" onClick={() => moveItem(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
            <IconButton size="small" aria-label="Move down" onClick={() => moveItem(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
          </>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
          The order parents see areas, consortium groups and papers on Practice Papers, Mock Exams and the school finder.
          Rank follows this page. Most pupils sitting lists schools by how many students have them as a target.
          Exam date soonest puts the schools (and their packs) whose next 11+ exam is closest at the top.
          Drag the handle or use the arrows.
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <FormControlLabel
            control={
              <Switch
                checked={consortiumFirst}
                onChange={(e) => {
                  setConsortiumFirst(e.target.checked);
                  setDirty(true);
                }}
              />
            }
            label="Consortium groups first"
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={schoolSort}
            onChange={(_, value) => {
              if (!value) return;
              setSchoolSort(value);
              setDirty(true);
            }}
            aria-label="School order"
          >
            <ToggleButton value="rank" sx={{ textTransform: "none" }}>Rank</ToggleButton>
            <ToggleButton value="pupils" sx={{ textTransform: "none" }}>Most pupils sitting</ToggleButton>
            <ToggleButton value="exam_date" sx={{ textTransform: "none" }}>Exam date soonest</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            select
            size="small"
            label="Default school type"
            value={defaultSchoolCategory}
            onChange={(e) => {
              setDefaultSchoolCategory(e.target.value);
              setDirty(true);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="independent">Independent</MenuItem>
            <MenuItem value="grammar">Grammar</MenuItem>
            <MenuItem value="both">Both</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
          <Button variant="contained" onClick={saveOrder} disabled={saving || !dirty} sx={{ textTransform: "none", fontWeight: 700 }}>
            {saving ? "Saving…" : "Save order"}
          </Button>
        </Box>
      </Box>

      {dirty && <Alert severity="info">Unsaved changes to the area, group, or school order.</Alert>}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" }, gap: 2, alignItems: "start" }}>
        <Paper sx={CARD_SX}>
          <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary" }}>1 · Areas</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 640, overflow: "auto" }}>
            <SortableList
              items={regions}
              getId={(r) => r.id}
              onMove={dragRegion}
              renderItem={(r, i, handle) => (
                <OrderRow
                  handle={handle}
                  selected={r.id === selectedRegionId}
                  onSelect={() => setSelectedRegionId(r.id)}
                  onUp={() => moveRegion(i, -1)}
                  onDown={() => moveRegion(i, 1)}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                    {i + 1}. {r.name} <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>({r.school_count})</Box>
                  </Typography>
                </OrderRow>
              )}
            />
          </Box>
        </Paper>

        <Paper sx={CARD_SX}>
          <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary" }}>
            2 · Groups in {region?.name || "—"}
          </Typography>
          {regionGroups.length === 0 && (
            <Typography variant="body2" color="text.secondary">No consortium in this area.</Typography>
          )}
          <SortableList
            items={regionGroups}
            getId={(g) => g.id}
            onMove={dragGroup}
            renderItem={(g, i, handle) => (
              <OrderRow
                handle={handle}
                highlight
                selected={schoolScope === "consortium" && g.id === selectedConsortiumId}
                onSelect={() => {
                  setSelectedConsortiumId(g.id);
                  setSchoolScope("consortium");
                }}
                onUp={() => moveGroup(g.id, -1)}
                onDown={() => moveGroup(g.id, 1)}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Chip size="small" label="Consortium" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                    {g.name} <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>({g.school_count})</Box>
                  </Typography>
                </Box>
              </OrderRow>
            )}
          />
          {region && (region.other_schools || []).length > 0 && (
            <OrderRow selected={schoolScope === "other"} onSelect={() => setSchoolScope("other")}>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                Other schools in {region.name}{" "}
                <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>({region.other_schools.length})</Box>
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {consortiumFirst ? "Listed after the consortium groups" : "Listed before the consortium groups"}
              </Typography>
            </OrderRow>
          )}
        </Paper>

        <Paper sx={CARD_SX}>
          <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary" }}>
            3 · Schools
            {schoolScope === "other"
              ? ` · other in ${region?.name || "—"}`
              : ` · ${regionGroups.find((g) => g.id === selectedConsortiumId)?.name || "—"}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {schoolSort === "pupils"
              ? "Ordered by how many students list the school as a target. Rank arrows are off in this view."
              : schoolSort === "exam_date"
              ? "Ordered by each school's next exam date (Schools › admission cycles). Schools with no date follow in rank order. Rank arrows are off in this view."
              : "This is the rank parents see. Move a school, then save."}
          </Typography>
          {schoolRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No schools in this group.</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 640, overflow: "auto" }}>
              <SortableList
                items={schoolRows}
                getId={(school) => school.id}
                onMove={schoolSort === "rank" ? dragSchool : () => {}}
                renderItem={(school, i, handle) => (
                <OrderRow
                  handle={schoolSort === "rank" ? handle : null}
                  onUp={schoolSort === "rank" ? () => moveSchool(i, -1) : undefined}
                  onDown={schoolSort === "rank" ? () => moveSchool(i, 1) : undefined}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, minWidth: 0 }} noWrap title={school.name}>
                      {i + 1}. {school.name}
                    </Typography>
                    <Chip size="small" label={school.category || "Other"} variant="outlined" sx={{ fontWeight: 700 }} />
                    <Typography sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
                      {school.pupil_count} pupil{school.pupil_count === 1 ? "" : "s"}
                    </Typography>
                    {school.next_exam_date ? (
                      <Typography sx={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", color: examSoon(school.next_exam_date) }}>
                        Exam {new Date(`${school.next_exam_date}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </Typography>
                    ) : null}
                  </Box>
                </OrderRow>
                )}
              />
            </Box>
          )}
        </Paper>

        <Paper sx={CARD_SX}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary" }}>4 · Pin to the top</Typography>
            <ToggleButtonGroup size="small" exclusive value={itemType} onChange={(_, v) => v && setItemType(v)}>
              <ToggleButton value="papers" sx={{ textTransform: "none" }}>Papers</ToggleButton>
              <ToggleButton value="mock_exams" sx={{ textTransform: "none" }}>Mock exams</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Pinned items show first on the site, in this order. Unpinned ones follow, newest first.
          </Typography>
          {itemsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>
          ) : items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {selectedConsortiumId ? "Nothing linked to this group's schools yet." : "Pick a consortium group."}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 560, overflow: "auto" }}>
              <SortableList
                items={items.slice(0, pinnedCount)}
                getId={(item) => item.id}
                onMove={dragItem}
                renderItem={(item, i, handle) => renderItemRow(item, i, handle)}
              />
              {items.slice(pinnedCount).map((item, k) => renderItemRow(item, pinnedCount + k, null))}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

/** The shop pages admin can lay out, with a name for each section (keys from DisplayOrderService::PAGE_SECTIONS). */
const PAGES = {
  mock_exams: {
    name: "Mock Exams",
    path: "/mock-exams",
    views: true,
    labels: {
      hero: "Hero · pick your school",
      filters: "Filters",
      featured: "Most popular",
      jump: "Jump to school",
      groups: "School sections",
      how_it_works: "How it works",
      faq: "FAQ",
    },
  },
  practice_papers: {
    name: "Practice Papers",
    path: "/practice-papers",
    views: true,
    labels: {
      hero: "Hero · pick your school, Practice | Past tabs",
      filters: "Filters",
      featured: "Most popular",
      areas: "Browse by exam area",
      packs: "Practice packs",
      papers: "Single papers",
    },
  },
  find_a_school: {
    name: "Find a School",
    path: "/find-a-school",
    views: false,
    labels: { hero: "Postcode search", results: "Results by area", map: "Map" },
  },
};

/** Rough height of each section in the preview, so the sketch reads like the page. */
const PREVIEW_HEIGHT = { hero: 46, groups: 90, papers: 80, packs: 60, results: 80, map: 60, featured: 44, areas: 40 };

/** Page layout tab: each shop page's sections (drag, switch on or off), default view, first area. */
function PageLayout() {
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState({});
  const [regions, setRegions] = useState([]);
  const [siteUrl, setSiteUrl] = useState("");
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState("");

  const apply = (d) => {
    setPages(d.pages || {});
    setRegions(d.regions || []);
    setSiteUrl(String(d.site_url || "").replace(/\/$/, ""));
  };

  useEffect(() => {
    api
      .get("admin/display-order/layout")
      .then((res) => apply(res.data?.data || {}))
      .catch((e) => toast.error(e.response?.data?.message || "Could not load the page layout."))
      .finally(() => setLoading(false));
  }, []);

  const change = (page, patch) => {
    setPages((prev) => ({ ...prev, [page]: { ...prev[page], ...patch } }));
    setDirty((prev) => ({ ...prev, [page]: true }));
  };

  const save = async (page) => {
    const layout = pages[page];
    setSaving(page);
    try {
      const res = await api.put("admin/display-order/layout", {
        page,
        sections: layout.sections,
        view: layout.view,
        open_area_id: layout.open_area_id || null,
      });
      apply(res.data?.data || {});
      setDirty((prev) => ({ ...prev, [page]: false }));
      toast.success(`${PAGES[page].name} layout saved.`);
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not save the layout.");
    } finally {
      setSaving("");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
        What each shop page shows and in what order. The hero always sits at the top. Switch a section off to hide it.
        Parents can still switch between Cards and List; this sets the view the page opens on.
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" }, gap: 2, alignItems: "start" }}>
        {Object.entries(PAGES).map(([page, meta]) => {
          const layout = pages[page];
          if (!layout) return null;
          // The hero is pinned first; only the sections below it can be dragged.
          const hero = layout.sections.find((s) => s.key === "hero");
          const rest = layout.sections.filter((s) => s.key !== "hero");
          const setSections = (next) => change(page, { sections: [...(hero ? [hero] : []), ...next] });
          const toggle = (key) =>
            change(page, { sections: layout.sections.map((s) => (s.key === key ? { ...s, on: !s.on } : s)) });
          const row = (section, handle) => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minHeight: 44,
                px: 1,
                borderRadius: 2,
                border: "1px solid #E2E8F0",
                bgcolor: section.on ? "#fff" : "#F8FAFC",
              }}
            >
              {handle || <Box sx={{ width: 34 }} />}
              <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 600, color: section.on ? "text.primary" : "text.disabled" }}>
                {meta.labels[section.key] || section.key}
              </Typography>
              <Switch
                size="small"
                checked={section.on}
                onChange={() => toggle(section.key)}
                inputProps={{ "aria-label": `Show ${meta.labels[section.key] || section.key}` }}
              />
            </Box>
          );
          return (
            <Paper key={page} sx={CARD_SX}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 800 }}>{meta.name}</Typography>
                {siteUrl ? (
                  <Button
                    size="small"
                    href={`${siteUrl}${meta.path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    sx={{ textTransform: "none" }}
                  >
                    {meta.path}
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">{meta.path}</Typography>
                )}
              </Box>
              <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary", lineHeight: 1.5 }}>Sections · drag to reorder</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {hero ? row(hero, null) : null}
                <SortableList
                  items={rest}
                  getId={(s) => s.key}
                  onMove={(from, to) => setSections(arrayMove(rest, from, to))}
                  renderItem={(section, i, handle) => row(section, handle)}
                />
              </Box>

              {meta.views ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Opens as</Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={layout.view}
                    onChange={(_, v) => v && change(page, { view: v })}
                    aria-label={`${meta.name} default view`}
                  >
                    <ToggleButton value="cards" sx={{ textTransform: "none" }}>Cards</ToggleButton>
                    <ToggleButton value="list" sx={{ textTransform: "none" }}>List</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              ) : (
                <TextField
                  select
                  size="small"
                  label="Area opened first"
                  value={layout.open_area_id ?? ""}
                  onChange={(e) => change(page, { open_area_id: e.target.value === "" ? null : Number(e.target.value) })}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 1 }}
                  helperText="When no postcode is entered. Default: the first area in the order above."
                >
                  <MenuItem value="">First area</MenuItem>
                  {regions.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                </TextField>
              )}

              <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary", lineHeight: 1.5, mt: 1 }}>Preview</Typography>
              <Box aria-hidden="true" sx={{ p: 1, borderRadius: 2, bgcolor: "#F8FAFC", border: "1px solid #E2E8F0", display: "flex", flexDirection: "column", gap: 0.5 }}>
                {layout.sections.map((s) => (
                  <Box
                    key={s.key}
                    sx={{
                      height: s.on ? PREVIEW_HEIGHT[s.key] || 26 : 22,
                      borderRadius: 1,
                      px: 1,
                      display: "flex",
                      alignItems: "center",
                      fontSize: 11,
                      fontWeight: 700,
                      color: s.on ? "#5A6478" : "#94A3B8",
                      bgcolor: s.on ? (s.key === "hero" ? "#EEF0FA" : s.key === "featured" ? "#FFF7F6" : "#fff") : "transparent",
                      border: s.on ? "1px solid #E2E8F0" : "1px dashed #CBD5E1",
                    }}
                  >
                    {meta.labels[s.key] || s.key}
                    {s.on ? "" : " (hidden)"}
                    {s.on && (s.key === "groups" || s.key === "packs" || s.key === "papers") && meta.views ? ` · ${layout.view}` : ""}
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained"
                onClick={() => save(page)}
                disabled={!dirty[page] || saving === page}
                sx={{ textTransform: "none", fontWeight: 700, mt: 1, alignSelf: "flex-start" }}
              >
                {saving === page ? "Saving…" : `Save ${meta.name}`}
              </Button>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}

const BADGE_OPTIONS = [
  { value: "popular", label: "Popular", bg: "#D9251A" },
  { value: "best_value", label: "Best value", bg: "#1E7A5F" },
  { value: "new", label: "New", bg: "#4450A5" },
  { value: "featured", label: "Featured", bg: "#6D28D9" },
  { value: "none", label: "No badge", bg: "#94A3B8" },
];

/** What can be featured on each page: mock bundles and mock exams, or paper packs and papers. */
const FEATURE_TYPES = {
  mock_exams: [
    { value: "pack", kind: "mock", label: "Bundles" },
    { value: "mock_exam", label: "Mock exams" },
  ],
  practice_papers: [
    { value: "pack", kind: "paper", label: "Packs" },
    { value: "paper", label: "Papers" },
  ],
};

/** Featured & badges tab: what the "Most popular" strip shows, in order, and each item's badge. */
function Featured() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState("mock_exams");
  const [type, setType] = useState("pack");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("admin/display-order/featured")
      .then((res) => setItems(res.data?.data?.items || []))
      .catch((e) => toast.error(e.response?.data?.message || "Could not load featured items."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setType(FEATURE_TYPES[page][0].value);
  }, [page]);

  const typeMeta = FEATURE_TYPES[page].find((t) => t.value === type) || FEATURE_TYPES[page][0];

  useEffect(() => {
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      api
        .get("admin/display-order/search", { params: { type: typeMeta.value, q: query || undefined, kind: typeMeta.kind } })
        .then((res) => {
          if (!cancelled) setResults(res.data?.data?.items || []);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [typeMeta.value, typeMeta.kind, query]);

  const onPage = items.filter((i) => i.page === page);
  const isFeatured = (r) => onPage.some((i) => i.type === r.type && i.id === r.id);

  const replacePage = (next) => {
    setItems((prev) => [...prev.filter((i) => i.page !== page), ...next]);
    setDirty(true);
  };
  const add = (r) => replacePage([...onPage, { ...r, page, badge: "popular" }]);
  const remove = (r) => replacePage(onPage.filter((i) => !(i.type === r.type && i.id === r.id)));
  const setBadge = (r, badge) => replacePage(onPage.map((i) => (i.type === r.type && i.id === r.id ? { ...i, badge } : i)));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("admin/display-order/featured", {
        items: items.map(({ page: p, type: t, id, badge }) => ({ page: p, type: t, id, badge })),
      });
      setItems(res.data?.data?.items || []);
      setDirty(false);
      toast.success("Featured items saved.");
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not save featured items.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
          Featured items show in the “Most popular” strip at the top of the page, in this order (the first four show).
          Their badge also shows on the item&apos;s own card. Items that are inactive or filtered out are skipped on the site.
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ToggleButtonGroup size="small" exclusive value={page} onChange={(_, v) => v && setPage(v)} aria-label="Page">
            <ToggleButton value="mock_exams" sx={{ textTransform: "none" }}>Mock Exams</ToggleButton>
            <ToggleButton value="practice_papers" sx={{ textTransform: "none" }}>Practice Papers</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" onClick={save} disabled={!dirty || saving} sx={{ textTransform: "none", fontWeight: 700 }}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Box>
      {dirty && <Alert severity="info">Unsaved changes to the featured items.</Alert>}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(0, 3fr)" }, gap: 2, alignItems: "start" }}>
        <Paper sx={CARD_SX}>
          <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary" }}>Add an item</Typography>
          <ToggleButtonGroup size="small" exclusive value={type} onChange={(_, v) => v && setType(v)} aria-label="Item type">
            {FEATURE_TYPES[page].map((t) => (
              <ToggleButton key={t.value} value={t.value} sx={{ textTransform: "none" }}>{t.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            size="small"
            placeholder="Search by name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ color: "text.secondary", mr: 1 }} /> }}
            inputProps={{ "aria-label": "Search items" }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 520, overflow: "auto" }}>
            {searching && !results.length ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
            ) : results.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Nothing found.</Typography>
            ) : (
              results.map((r) => (
                <Box key={`${r.type}-${r.id}`} sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 48, px: 1, borderRadius: 2, border: "1px solid #E2E8F0" }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap title={r.name}>{r.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>{r.meta}</Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={isFeatured(r)}
                    onClick={() => add(r)}
                    sx={{ textTransform: "none", fontWeight: 700, flex: "none" }}
                  >
                    {isFeatured(r) ? "Featured" : "+ Feature"}
                  </Button>
                </Box>
              ))
            )}
          </Box>
        </Paper>

        <Paper sx={CARD_SX}>
          <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary" }}>
            Featured on {page === "mock_exams" ? "Mock Exams" : "Practice Papers"} · drag to reorder
          </Typography>
          {onPage.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Nothing featured yet. The strip is hidden until you add an item.</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              <SortableList
                items={onPage}
                getId={(i) => `${i.type}-${i.id}`}
                onMove={(from, to) => replacePage(arrayMove(onPage, from, to))}
                renderItem={(item, i, handle) => {
                  const badge = BADGE_OPTIONS.find((b) => b.value === item.badge) || BADGE_OPTIONS[0];
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minHeight: 56,
                        px: 1,
                        borderRadius: 2,
                        border: "1px solid #C7CBF0",
                        bgcolor: i < 4 ? "#F7F8FE" : "#fff",
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                      }}
                    >
                      {handle}
                      <Typography sx={{ width: 20, fontSize: 13, fontWeight: 700, color: "text.secondary" }}>{i + 1}</Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap title={item.name}>{item.name}</Typography>
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>
                          {item.meta}{i >= 4 ? " · not shown (only the first four show)" : ""}
                        </Typography>
                      </Box>
                      <TextField
                        select
                        size="small"
                        label="Badge"
                        value={badge.value}
                        onChange={(e) => setBadge(item, e.target.value)}
                        sx={{ minWidth: 140 }}
                      >
                        {BADGE_OPTIONS.map((b) => (
                          <MenuItem key={b.value} value={b.value}>
                            <Box component="span" sx={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", bgcolor: b.bg, mr: 1 }} />
                            {b.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <IconButton size="small" aria-label="Remove from featured" onClick={() => remove(item)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

const TABS = [
  { key: "schools", label: "Schools & areas" },
  { key: "layout", label: "Page layout" },
  { key: "featured", label: "Featured & badges" },
];

/**
 * Admin → Display order: what parents see on Mock Exams, Practice Papers and Find a School, and
 * in what order. Schools & areas (21 Oct 2026 meeting), plus Page layout and Featured & badges
 * (shop redesign, 29 Oct 2026). The tab is kept in the URL hash so a reload stays on it.
 */
export default function DisplayOrder() {
  const initial = TABS.findIndex((t) => `#${t.key}` === window.location.hash);
  const [tab, setTab] = useState(initial >= 0 ? initial : 0);
  const selectTab = (_, value) => {
    setTab(value);
    window.history.replaceState(null, "", `#${TABS[value].key}`);
  };
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 800 }}>Display order</Typography>
      <Tabs value={tab} onChange={selectTab} aria-label="Display order sections" sx={{ borderBottom: "1px solid #E2E8F0" }} variant="scrollable" allowScrollButtonsMobile>
        {TABS.map((t) => (
          <Tab key={t.key} label={t.label} sx={{ textTransform: "none", fontWeight: 700 }} />
        ))}
      </Tabs>
      {tab === 0 ? <SchoolsAndAreas /> : tab === 1 ? <PageLayout /> : <Featured />}
    </Box>
  );
}
